import type {
  Poll,
  Program,
  ProgramActivityContentObjectList,
  ProgramPollContentObjectList,
  ProgramTimelineContentObject,
} from 'models';
import { can } from 'old/utils/permissions';
import { useCallback, useMemo, useState } from 'react';
import { canReportLegacyContentObject, usePermissions } from 'permissions';
import { Icon } from 'ui';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- We should not imoprt service, needs refactor
import { getEvent } from 'features/events/services';
import type { Activity, PeriodicityType } from 'models/activity';
import type { EventContentObjectDataObject } from 'models/service';
import { useProgramTimelineActions } from '../../../hooks';

const toActivityDraft = (item: ProgramActivityContentObjectList): Activity => ({
  ...item.activity,
  description: item.activity.description ?? '',
  htmlDescription: item.activity.htmlDescription ?? '',
  nextPeriodDate: item.activity.nextPeriodDate ?? null,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Backend documents periodicity_type as a plain string; EditActivity needs the narrower literal union
  periodicityType: item.activity.periodicityType as PeriodicityType,
  programsTopicId: item.programsTopic.id,
  times: item.activity.times ?? [],
});

const toPollDraft = (item: ProgramPollContentObjectList): Poll => ({
  attachments: item.attachments,
  description: item.poll.description,
  endTime: item.poll.endTime,
  htmlDescription: item.poll.htmlDescription,
  id: item.poll.id,
  pinned: item.pinned,
  postInCommunities: [],
  programsTopicId: item.programsTopic.id,
  questions: item.poll.questions,
  startTime: item.poll.startTime,
  title: item.poll.title,
});

type Parameters = {
  item: ProgramTimelineContentObject;
  program: Program;
};

/**
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
const useProgramTimelineItemHeaderActions = ({ item, program }: Parameters) => {
  const { canDeleteEvent, canEditEvent } = usePermissions();
  const viewer = useUser();
  const { bookmarkObject, contextHolder, deleteCurriculumObject, isBookmarking, reportObject } =
    useProgramTimelineActions();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [eventDraft, setEventDraft] = useState<unknown>();
  const [inviteesEventId, setInviteesEventId] = useState<string>();

  const closeEditModal = useCallback(() => {
    setIsEditOpen(false);
    setEventDraft(undefined);
  }, []);

  const closeInviteesModal = useCallback(() => {
    setInviteesEventId(undefined);
  }, []);

  const onBookmarkClick = useCallback(() => {
    void bookmarkObject(item);
  }, [bookmarkObject, item]);

  const onMenuAction = useCallback(
    (key: string) => {
      switch (key) {
        case 'delete':
          deleteCurriculumObject({ object: item });
          break;

        case 'edit':
          if (item.type === 'event') {
            void (async () => {
              const response = await getEvent<EventContentObjectDataObject>(item.event.id);

              setEventDraft({ ...response.data, type: item.type });
              setIsEditOpen(true);
            })();
          } else {
            setIsEditOpen(true);
          }
          break;

        case 'invite':
          if (item.type === 'event') {
            setInviteesEventId(item.event.id);
          }
          break;

        case 'report':
          reportObject(item);
          break;
      }
    },
    [deleteCurriculumObject, item, reportObject],
  );

  const activityDraft = useMemo(() => (item.type === 'activity' ? toActivityDraft(item) : undefined), [item]);
  const pollDraft = useMemo(() => (item.type === 'poll' ? toPollDraft(item) : undefined), [item]);

  const actions = useMemo(() => {
    const result = [];

    if (
      item.type === 'event'
        ? canEditEvent({ contentObject: item, view: 'program' })
        : can(program, 'Program', 'editProgramContent')
    ) {
      result.push({ icon: <Icon icon="pencil-line" size="small" />, key: 'edit', label: 'Edit' });
    }

    if (item.type === 'event') {
      result.push({ icon: <Icon icon="user-1-line" size="small" />, key: 'invite', label: 'See invitees' });
    }

    if (canReportLegacyContentObject({ contentObject: program, viewer })) {
      result.push({ icon: <Icon icon="flag-1-line" size="small" />, key: 'report', label: 'Report' });
    }

    if (
      item.type === 'event'
        ? canDeleteEvent({ contentObject: item, view: 'program' })
        : can(program, 'Program', 'deleteProgramContent')
    ) {
      result.push({
        danger: true,
        icon: <Icon color="error" icon="delete-2-line" size="small" />,
        key: 'delete',
        label: 'Delete',
      });
    }

    return result;
  }, [item, program, canEditEvent, canDeleteEvent, viewer]);

  return useMemo(
    () => ({
      actions,
      contextHolder,
      editModal: {
        activityDraft,
        eventDraft,
        isOpen: isEditOpen,
        onCancel: closeEditModal,
        onSuccess: closeEditModal,
        pollDraft,
      },
      inviteesEventId,
      isBookmarking,
      onBookmarkClick,
      onCloseInvitees: closeInviteesModal,
      onMenuAction,
    }),
    [
      actions,
      activityDraft,
      closeEditModal,
      closeInviteesModal,
      contextHolder,
      eventDraft,
      inviteesEventId,
      isBookmarking,
      isEditOpen,
      onBookmarkClick,
      onMenuAction,
      pollDraft,
    ],
  );
};

export default useProgramTimelineItemHeaderActions;
